import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';
import type { RoomRateModel } from '@elara/client-core/models/RoomRateModel';
import type { HotelData } from '@elara/domain/models/schemas/hotel-schemas';

import { useEffect, useState } from 'react';

import { formatOccupancy } from '@elara/client-core/lib/occupancy-utils';
import { orderRatesForParty } from '@elara/client-core/lib/rate-order';
import { roomPricingNotice } from '@elara/client-core/lib/room-pricing-notice';
import { rateDepositLines } from '@elara/client-core/lib/room-utils';
import { cn, formatPrice, metadataForIdentity, pluralize, roomAvailabilityIcon } from '@elara/client-core/lib/utils';
import { MEAL_COMBINATIONS } from '@elara/client-core/models/RoomRateModel';

import { useCanRefreshPricing } from '#lib/can-refresh-pricing.js';
import { useRefreshPricing } from '#lib/useRefreshPricing.js';
import { useAppState, useItineraryState, useUserProfileState } from '#states/hooks.js';
import { Button } from '#ui';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '#ui/tooltip.js';

import { Alert, AlertDescription } from './alert.js';
import { SimpleMarkdown } from './chat/SimpleMarkdown.js';
import { PriceStatusBanner } from './PriceStatusBanner.js';
import { PriceStatusIndicator } from './PriceStatusIndicator.js';
import { RoomCountControl } from './room-count/RoomCountControl.js';
import { UnavailableOverlay } from './UnavailableOverlay.js';

/** The rates grid: one column, two in a wider panel, three in a wide one. Shared with its skeleton. */
const RATE_GRID = 'grid grid-cols-1 @lg:grid-cols-2 @5xl:grid-cols-3 gap-4';

/** The rate card's shell, shared with its skeleton so the two take the same space. */
const RATE_CARD = '@container w-full flex flex-col gap-4 p-6 rounded-md bg-card shadow-xs';

interface RatePickerProps {
  room: RoomOptionModel;
  hotel: HotelData;
  nightsCount: number;
}

/**
 * One rate of a room: its name and policies, the price, and the rate's room count control under the price.
 */
export function RateCard({
  rate,
  nightsCount,
  room,
  hotel,
  control,
}: {
  rate: RoomRateModel;
  nightsCount: number;
  room: RoomOptionModel;
  hotel: HotelData;
  /** Replaces the connected `RoomCountControl`, so Storybook can show each picker state without a trip. */
  control?: React.ReactNode;
}) {
  const itineraryState = useItineraryState();
  const { debugMode } = useAppState();
  const { isPrivileged } = useUserProfileState();
  const showProductCode = debugMode && isPrivileged;
  const mealCombination = MEAL_COMBINATIONS.find((combo) => combo.id === rate.mealCombinationId);
  const mealName = mealCombination?.name ?? 'No meals';

  // Identity is `(productCode, occupancy)` — Sabre rotates rateKey on every request,
  // so we never persist it. Occupancy comes from the rate (each rate carries the
  // occupancy it was priced for).
  const metadata = rate.occupancy
    ? { productCode: rate.productCode, occupancy: rate.occupancy }
    : { productCode: rate.productCode };

  const title = `${room.type} at ${hotel.name}`;

  // Prefer generated human-readable content when present
  const rateName = rate.generated?.name ?? rate.name;
  const rateDescription = rate.generated?.description ?? rate.description;
  const guaranteeText = rate.generated?.guaranteePolicy;

  const depositPolicies = rateDepositLines(rate);
  // The rate owns both halves of its cancellation copy: the headline (with its resolved deadline) and the
  // fuller explanation, which stays in the tooltip. One source, so this can't drift from the itinerary or
  // the checkout again.
  const cancellationLabel = rate.cancellationPolicyTitle;
  const cancellationPolicies = rate.cancellationPolicyDescription ? [rate.cancellationPolicyDescription] : [];

  const hasRateDetails =
    depositPolicies.length > 0 ||
    (guaranteeText != null && guaranteeText !== '') ||
    rate.prePaymentRequired ||
    rate.guaranteeRequired ||
    rate.loyaltyPoints ||
    rate.notes ||
    (rate.generated?.requiresMembership === true && rate.generated?.membershipOrganizationName);

  const isRateUnavailable = rate.available === false;

  const identityMeta = metadataForIdentity('room', metadata);
  const roomItems =
    room.id != null ? itineraryState.getAllItineraryItemsByReference('room', room.id, identityMeta) : [];
  // Once this rate is already booked, prefer that item's own frozen policy text (via the centralized
  // `ItineraryItemModel.cancellationPolicyText`, captured at submit) over a summary derived from the
  // live — and possibly since-changed — rate, so this tooltip can't drift from what `ViewBookingDialog`
  // shows for that exact booking.
  const bookedItem = roomItems.find((item) => item.isBooked || item.isApproved || item.isCancellationPending);
  const cancellationDescription = bookedItem?.cancellationPolicyText ?? cancellationPolicies.join(', ');

  // How many of the room Elara's pick books at this rate's party: "Book 2 rooms" when it is more than one.
  const roomsToBook = room.roomsToBook(rate.occupancy);

  const cardContent = (
    <div className={RATE_CARD} data-product-code={rate.productCode}>
      <div>
        {/* Titled as the cards are (`DetailCardLayout`'s `feature` title), but wrapping: a rate's name is
            what tells it from the next ("Advance Purchase, Breakfast Included"), so it is never cut short. */}
        <h3 className="m-0 font-sans text-base leading-tight font-medium">{rateName ?? 'Rate'}</h3>
        {showProductCode && (
          <div className="text-2xs font-mono text-muted-foreground mt-0.5">Sabre Product Code: {rate.productCode}</div>
        )}
        {rateDescription && (
          <div className="text-xs text-muted-foreground max-w-prose text-pretty mt-1 mb-1">
            <SimpleMarkdown text={rateDescription} />
          </div>
        )}
      </div>

      {/* Icons top-aligned: at 20px they match the text's line height, so a row that wraps keeps its icon
          on the first line rather than centred on the block. */}
      <div className="flex-1 flex flex-col gap-2 text-sm max-w-prose">
        {/* First, who the rate is priced for. */}
        {rate.occupancy && (
          <div className="flex items-start gap-2">
            <MaterialIcon icon="group" size={IconSizes.sm} />
            {/* The party without the children's ages: the search and checkout have them. */}
            <span>{formatOccupancy({ ...rate.occupancy, childrenAges: undefined })}</span>
          </div>
        )}
        {/* Then, amber on a row of its own, how many rooms to book when one room does not take everyone. */}
        {roomsToBook > 1 && (
          <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
            <MaterialIcon icon="bed" size={IconSizes.sm} />
            <span>Book {roomsToBook} rooms to accommodate all guests</span>
          </div>
        )}

        <div
          className={cn(
            'flex items-start gap-2 text-pretty',
            rate.refundStatus === 'refundable' && !rate.isCancellationDeadlinePassed && 'text-success',
          )}
        >
          {/* Unconfirmed: the search's estimate only, until Rules has been read — claim nothing. */}
          <MaterialIcon
            icon={
              rate.refundStatus === 'unconfirmed' ? 'schedule' : rate.isRefundable ? 'currency_exchange' : 'money_off'
            }
            size={IconSizes.sm}
          />
          {cancellationDescription ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    {cancellationLabel}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  {cancellationDescription}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span>{cancellationLabel}</span>
          )}
        </div>

        {rate.hasMeals && (
          <div className="flex items-start gap-2">
            <MaterialIcon icon="restaurant" size={IconSizes.sm} />
            {mealName} included
          </div>
        )}

        {hasRateDetails && (
          <>
            {depositPolicies.length > 0 &&
              depositPolicies.map((policy, index) => (
                <div className="flex items-start gap-2 text-pretty" key={`deposit-policy-${index}`}>
                  <MaterialIcon icon="credit_card" size={IconSizes.sm} />
                  <span>{policy}</span>
                </div>
              ))}

            {rate.loyaltyPoints && rate.loyaltyProgramName && (
              <div className="flex items-start gap-2">
                <MaterialIcon icon="stars_2" size={IconSizes.sm} />
                <span>Earn {rate.loyaltyProgramName} points with this stay</span>
              </div>
            )}
          </>
        )}

        {/* Who may book it, when not everyone may — the condition a military or members-only rate carries,
            in warning text at the end of the list so nobody books a rate the hotel refuses at
            arrival. */}
        {rate.eligibility && (
          <div className="flex items-start gap-2 text-pretty text-amber-700 dark:text-amber-400">
            <MaterialIcon icon="badge" size={IconSizes.sm} />
            <span>{rate.eligibility}</span>
          </div>
        )}

        {rate.notes && <div className="text-2xs text-muted-foreground">{rate.notes}</div>}
      </div>

      <div className="flex flex-col gap-4 mt-1">
        {/* A sold-out rate keeps its price for comparison, struck through. */}
        <div className="flex flex-col">
          <div className="flex items-baseline gap-1">
            <div className={cn('text-lg font-medium', isRateUnavailable && 'line-through')}>
              {formatPrice(rate.localPricing.avgNightlyRate, rate.localPricing.currency)}
            </div>
            <div className="whitespace-nowrap text-xs">/ night</div>
            {/* The price's status (Refresh, or how it has moved) beside the price it is about, on its baseline. */}
            <div className="ml-2">
              <PriceStatusIndicator
                type="room"
                model={room}
                productCode={rate.productCode}
                occupancy={rate.occupancy}
                priceDivisor={room.nightsCount || 1}
                priceUnit="per night"
              />
            </div>
          </div>

          {/* The stay's total. What it includes (taxes, fees due at the property) is spelled out at checkout. */}
          <div className="text-xs text-muted-foreground">
            <span className={cn(isRateUnavailable && 'line-through')}>
              {formatPrice(rate.localPricing.totalPrice, rate.localPricing.currency)}
            </span>{' '}
            for {nightsCount} {pluralize(nightsCount, 'night')}
          </div>
        </div>

        {/* One control for the rate's rooms, under the price: "Add room", then minus / count / plus with
            Book, or Booked once everything added is booked. Stays usable on a sold-out rate so a planned room
            can still be removed and a booking still viewed; plus is what the rate being gone disables. */}
        {control ?? (
          <RoomCountControl
            room={room}
            title={title}
            metadata={metadata}
            identityMeta={identityMeta}
            className="z-10"
          />
        )}
      </div>
    </div>
  );

  if (isRateUnavailable) {
    return <UnavailableOverlay className="w-full shadow-xs">{cardContent}</UnavailableOverlay>;
  }

  return cardContent;
}

/**
 * Asks the hotel again about every rate of the room: the recheck the price line runs, which refreshes prices,
 * sold-out rates and how many rooms are left. So it is also how a room at the hotel's limit learns it has
 * more. Says what changed in a toast. Hidden where the recheck is refused (travel date passed, or a room here
 * already booked).
 */
export function RefreshRatesButton({ room }: { room: RoomOptionModel }) {
  const canRecheck = useCanRefreshPricing('room', room);
  const { isRefreshing, refresh } = useRefreshPricing({
    type: 'room',
    referenceId: room.id,
    enabled: canRecheck,
    label: room.displayName,
  });
  if (!canRecheck) return null;

  return (
    // A link on the stay's line, sized to its text rather than a button's box. Its own span sets the icon as the
    // line's metadata items set theirs (`gap-1`): the button's inner span has a fixed `gap-2`.
    <Button variant="link" isBusy={isRefreshing} onClick={() => void refresh()}>
      <span className="flex items-baseline gap-1">
        <MaterialIcon icon="refresh" size={IconSizes.sm} className="self-center" />
        Refresh
      </span>
    </Button>
  );
}

function SkeletonLine({ className }: { className?: string }) {
  return <div className={cn('h-3 animate-pulse rounded bg-background-tertiary', className)} />;
}

/**
 * The rates while their details load: the grid of rate cards in outline — name, a line of description,
 * the policy rows, the price and the room control — so the rates arrive into the space they will take
 * rather than pushing the guide down. One card per rate the room has, up to a row's worth.
 */
export function RatesSkeleton({ count }: { count: number }) {
  return (
    <div className={RATE_GRID} role="status" aria-busy aria-label="Loading rates">
      {Array.from({ length: Math.max(1, Math.min(count, 3)) }, (_, index) => (
        <div key={index} className={RATE_CARD}>
          <div className="flex flex-col gap-2">
            <SkeletonLine className="h-5 w-2/3" />
            <SkeletonLine className="w-full" />
          </div>
          <div className="flex-1 flex flex-col gap-2.5">
            <SkeletonLine className="w-3/4" />
            <SkeletonLine className="w-1/2" />
            <SkeletonLine className="w-2/3" />
          </div>
          <div className="flex flex-col gap-4 mt-1">
            <div className="flex flex-col gap-1.5">
              <SkeletonLine className="h-5 w-1/3" />
              <SkeletonLine className="w-1/2" />
            </div>
            <div className="h-10 w-full animate-pulse rounded-sm bg-background-tertiary" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Beside the rates' heading: how many of the room the hotel has left, which caps adding rooms on the rate cards.
 * The dates are in the heading (`ratesHeading`); who each rate is priced for is its rate card's to say.
 */
export function RateStay({ room }: { room: RoomOptionModel }) {
  if (!room.maxAvailableQuantity) return null;
  return (
    <div className="flex items-baseline gap-1 whitespace-nowrap text-destructive">
      <MaterialIcon
        icon={roomAvailabilityIcon(room.maxAvailableQuantity)}
        size={IconSizes.sm}
        className="self-center"
      />
      <span>
        {room.maxAvailableQuantity} {pluralize(room.maxAvailableQuantity, 'room')} left
      </span>
    </div>
  );
}

/**
 * A room's rates in one list, the best fit for the searched party first (`orderRatesForParty`): each card says
 * who its rate is priced for and, when that means booking more than one room, how many. Notes on why the rates
 * are priced as they are (the searched party's sold out or never priced) sit above the list. Four at a time.
 */
export function RatePicker({ room, hotel, nightsCount }: RatePickerProps) {
  const itineraryState = useItineraryState();
  const [displayLimit, setDisplayLimit] = useState(4);
  // Back to the first four when the guide shows another room or stay.
  useEffect(() => {
    setDisplayLimit(4);
  }, [room.id, nightsCount]);

  // Matched as each card's room count control matches its items, so a copy with rooms on the trip keeps its card.
  const isOnTrip = (rate: RoomRateModel) =>
    room.id != null &&
    itineraryState.getAllItineraryItemsByReference(
      'room',
      room.id,
      metadataForIdentity('room', { productCode: rate.productCode, occupancy: rate.occupancy }),
    ).length > 0;
  const { rates, parties } = orderRatesForParty(room.allRates, room.search?.occupancy, isOnTrip);
  if (rates.length === 0) return null;
  // Why another party's rates stand in for the searched party's, once each (see `roomPricingNotice`).
  const notes = [
    ...new Set(parties.map((party) => roomPricingNotice(room, party)).filter((note): note is string => !!note)),
  ];

  return (
    <div className="flex flex-col items-stretch gap-4">
      <PriceStatusBanner type="room" model={room} />

      {notes.map((note) => (
        <Alert key={note} variant="default">
          <AlertDescription>{note}</AlertDescription>
        </Alert>
      ))}

      <div className={RATE_GRID}>
        {rates.slice(0, displayLimit).map((rate) => (
          <RateCard key={rate.rateKey} rate={rate} nightsCount={nightsCount} room={room} hotel={hotel} />
        ))}
      </div>
      {rates.length > displayLimit && (
        <Button variant="link" onClick={() => setDisplayLimit(rates.length)}>
          View {rates.length - displayLimit} more {pluralize(rates.length - displayLimit, 'rate')}
        </Button>
      )}
    </div>
  );
}
